import { capitalize } from 'lodash-es'
import {
  PhoneNumber,
  PhoneNumberType,
} from '@/legacy/types/entities/phoneNumbers'

/**
 * convert formatted phonenumber back to only digits
 * @param phoneNumber
 */
export function onlyDigits(phoneNumber: string) {
  return phoneNumber.replaceAll(/\D/g, '')
}

const PHONE_NUMBER_DIGITS = 10

/**
 * Returns the 10-digit phone number if the search text is a full US phone
 * number (punctuation and a leading +1/1 allowed), otherwise null.
 * @param text
 */
export function parsePhoneNumberSearch(text: string): string | null {
  const trimmed = text.trim()
  if (!/^[\d\s().+-]+$/.test(trimmed)) return null

  const digits = onlyDigits(trimmed)
  if (digits.length === PHONE_NUMBER_DIGITS + 1 && digits.startsWith('1')) {
    return digits.slice(1)
  }
  return digits.length === PHONE_NUMBER_DIGITS ? digits : null
}

/**
 * formats a phone number display string based on the PhoneNumber BE model
 * @param phoneNumberObj
 */
export function formatPhoneFromPhoneNumberObj(
  phoneNumberObj: Partial<PhoneNumber> | null | undefined
): string {
  /* eslint-disable @typescript-eslint/no-magic-numbers */
  if (!phoneNumberObj || !phoneNumberObj.phoneNumber) return ''

  let cleanNumber = phoneNumberObj.phoneNumber
  cleanNumber = cleanNumber.replaceAll(/[^0-9]/g, '') // remove  non-numeric characters
  const cleanCountryCode = phoneNumberObj.countryCode
    ? phoneNumberObj.countryCode.replaceAll(/[^0-9]/g, '')
    : ''

  let formattedNumber = cleanNumber

  if (cleanNumber.length === 10) {
    // format as (XXX) XXX-XXXX
    formattedNumber = `(${cleanNumber.slice(0, 3)}) ${cleanNumber.slice(
      3,
      6
    )}-${cleanNumber.slice(6)}`
  } else if (cleanNumber.length === 7) {
    // format as XXX-XXXX (no area code)
    formattedNumber = `${cleanNumber.slice(0, 3)}-${cleanNumber.slice(3)}`
  } else if (cleanNumber.length > 10) {
    // format numbers longer than 10 digits with assumed country code
    formattedNumber = `(${cleanNumber.slice(-10, -7)}) ${cleanNumber.slice(
      -7,
      -4
    )}-${cleanNumber.slice(-4)}`
  }

  // Prepend country code if available
  if (cleanCountryCode) {
    formattedNumber = `+${cleanCountryCode} ${formattedNumber}`
  }

  // Append extension if available
  return phoneNumberObj.extension
    ? `${formattedNumber} x${phoneNumberObj.extension}`
    : formattedNumber
  /* eslint-enable @typescript-eslint/no-magic-numbers */
}

/**
 * Cleans and formats phone number type to display in UI.
 * @param phoneType
 */
export function formatPhoneType(phoneType: PhoneNumberType) {
  if (phoneType === PhoneNumberType.VOIP) {
    return 'VoIp'
  }
  return capitalize(phoneType) || ''
}

/**
 * Cleans and formats a ten digit phone number.
 * @param phone
 */
export function formatPhone(phone: string | number) {
  if (!phone) return ''
  // eslint-disable-next-line @typescript-eslint/no-magic-numbers
  const cleaned = phone.toString().replaceAll(/\D/g, '').slice(-10)
  const match = cleaned.match(/^(\d{1,3})?(\d{1,3})?(\d{1,4})?$/)

  // if the number matches a 10 digit phone number (or part of one), return
  // the digits separated by '-', otherwise, return just the cleaned number
  return match ? match.slice(1).filter(Boolean).join('-') : cleaned
}
